import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Loading, Empty } from "../components/states";
import { Button, SectionHeading } from "../components/kit";
import { useBusyAction } from "../lib/useBusyAction";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Clinics — and adding the sixth.
 *
 * Demo checkbox 8, and the client's expansion story: "adding clinic #6 must be
 * config, not code." So this is a four-field form that takes under a minute,
 * and the new clinic is immediately real everywhere — requests, dispatch,
 * invoicing and statements all pick it up with no deploy.
 *
 * The form is deliberately still four fields and one button. Nothing here adds
 * a step: the desktop layout puts the fields two across so the whole form is
 * visible without scrolling, which is the only thing that changed about the
 * path from "Add a clinic" to a live clinic.
 */

export function Clinics() {
  const clinics = useQuery(api.clinics.list, { includeInactive: true });
  const create = useMutation(api.clinics.create);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const { busy, run } = useBusyAction();

  if (clinics === undefined)
    return <Loading rows={5} label="Loading clinics" />;

  const submit = () =>
    run(
      async () => {
        const result = await create({
          name,
          code,
          accountingEmail: email,
          shippingAddress: address,
        });
        // Clearing the form belongs on the success path, so a refused write
        // leaves what was typed where it was typed.
        setName("");
        setCode("");
        setEmail("");
        setAddress("");
        setOpen(false);
        return result;
      },
      {
        failed: "Could not add the clinic",
        done: (result) => ({
          happened: `${result.name} added`,
          next: "It can receive requests and invoices immediately.",
        }),
      },
    );

  const activeCount = clinics.filter((c) => c.active).length;

  return (
    <div className="space-y-4 pb-8">
      <SectionHeading
        title="Clinics"
        subtitle={`${activeCount} active`}
        action={
          !open ? (
            <Button onClick={() => setOpen(true)}>Add a clinic</Button>
          ) : undefined
        }
      />

      {open ? (
        /* The registry card with the accent on its border rather than a 2px
           ring around it: the form is the one live thing on the screen while it
           is open, and `--primary` is how this app says so now. `block gap-0
           p-4` is the shim over the `CardHeader`/`CardContent` slots this card
           does not use — its children lay themselves out. */
        <Card className="block gap-0 border-primary p-4">
          <h2 className="font-semibold">New clinic</h2>

          {/* Two across from `sm` up: four fields and a button, all visible at
              once. On a phone they stack, which is the same form it always
              was. */}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {/* `aria-label` stays on every field even though each now has a
                real `<label>`: `tools/demo.mjs` fills this form by
                `input[aria-label="Clinic name"]`, and checkbox 8 is the one
                that adds clinic #6 on stage. */}
            <div className="grid gap-1.5">
              <Label htmlFor="clinic-name">Name</Label>
              <Input
                id="clinic-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Clinic #6"
                aria-label="Clinic name"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="clinic-code">Short code</Label>
              <Input
                id="clinic-code"
                value={code}
                onChange={(event) => setCode(event.target.value.toUpperCase())}
                placeholder="C6"
                aria-label="Clinic code"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="clinic-email">Accounting email</Label>
              <Input
                id="clinic-email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="accounts@clinic.example"
                aria-label="Accounting email"
                inputMode="email"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="clinic-address">Shipping address</Label>
              <Input
                id="clinic-address"
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                placeholder="Vancouver, BC"
                aria-label="Shipping address"
              />
            </div>
          </div>

          {/* Stock sizes. This screen is Gloria at a desk, not an assistant on
              a phone, so the 40px `lg` the phone-critical actions carry would
              only make this button the largest thing on a page of fields. */}
          <div className="mt-4 flex gap-2">
            <Button
              onClick={submit}
              /* Name and code only: the server decides what else is required,
                 and a button that refuses to light up for a reason nobody
                 stated is worse than the error it prevents. */
              disabled={busy || !name.trim() || !code.trim()}
            >
              {busy ? "Adding…" : "Add clinic"}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      {clinics.length === 0 ? (
        /* No action button here on purpose: "Add a clinic" already sits in the
           header at every width, and two buttons carrying the same words is a
           second thing for the harness — and for Gloria — to pick between. */
        <Empty
          title="No clinics yet"
          body="Add the first one and it can receive requests and invoices immediately."
        />
      ) : (
        <div className="grid gap-2 lg:grid-cols-2">
          {clinics.map((clinic) => (
            <Card key={clinic._id} className="flex-row items-center gap-3 p-4">
              <span
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-md text-sm font-bold",
                  clinic.active
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {clinic.code}
              </span>
              <div className="min-w-0 flex-1">
                <div
                  className={
                    clinic.active
                      ? "font-medium"
                      : "font-medium text-muted-foreground"
                  }
                >
                  {clinic.name}
                </div>
                <div className="truncate text-xs text-muted-foreground">
                  {clinic.accountingEmail ?? clinic.shippingAddress}
                </div>
              </div>
              {!clinic.active ? (
                <span className="shrink-0 text-xs text-muted-foreground">
                  inactive
                </span>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
